import React, {useEffect, useState} from "react";
import {Card, CardBody, CardHeader, Container, Row, Col, Button} from "reactstrap";
import {useUser} from "../../contexts/AuthContext";
import awsconfig from "../../configs/awsconfig.json";

interface AccessControlProps {
  children: React.ReactNode;
}

const AccessControl: React.FC<AccessControlProps> = ({children}) => {
  const {user, isInitialized} = useUser();
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    // Skip access control if not enabled
    if (!awsconfig.Environment.accessControl?.enabled) {
      return;
    }

    // Wait for auth to finish loading
    if (!isInitialized || user.isLoading) {
      return;
    }

    // Check if user is authenticated
    if (!user.authenticated) {
      // User not logged in - they'll be redirected to login by AuthContext
      return;
    }

    // Check if user's email is in the whitelist
    const whitelist: string[] = awsconfig.Environment.accessControl.whitelist || [];
    const userEmail = user.email?.toLowerCase();

    if (!userEmail || !whitelist.includes(userEmail)) {
      setAccessDenied(true);
    }
  }, [user, isInitialized]);

  // Show loading state while checking access
  if (awsconfig.Environment.accessControl?.enabled && (!isInitialized || user.isLoading)) {
    return null; // Or a loading spinner
  }

  // Show access denied message
  if (accessDenied) {
    return (
      <div className="content" style={{marginTop: "100px"}}>
        <Container>
          <Row className="justify-content-center">
            <Col lg="8" md="10">
              <Card className="card-plain">
                <CardHeader>
                  <h2 className="text-center">Access Restricted</h2>
                </CardHeader>
                <CardBody>
                  <div className="text-center">
                    <i
                      className="tim-icons icon-lock-circle"
                      style={{fontSize: "5rem", marginBottom: "2rem", opacity: 0.5}}
                    ></i>

                    <h3>Development Environment Access Denied</h3>

                    <p style={{fontSize: "1.1rem", marginTop: "1.5rem", marginBottom: "2rem"}}>
                      This is a development environment and access is restricted to authorized users only.
                    </p>

                    <p style={{fontSize: "1rem", opacity: 0.8, marginBottom: "2rem"}}>
                      Your account (<strong>{user?.email}</strong>) is not authorized to access this environment.
                    </p>

                    <div style={{marginTop: "3rem"}}>
                      <Button
                        color="primary"
                        size="lg"
                        onClick={() => {
                          window.location.href = "https://zwiftalizer.com";
                        }}
                      >
                        <i className="tim-icons icon-minimal-right"></i> Go to Production Site
                      </Button>
                    </div>

                    <p style={{fontSize: "0.9rem", marginTop: "2rem", opacity: 0.6}}>
                      If you believe you should have access to this environment, please contact the administrator.
                    </p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Access granted - render children
  return <>{children}</>;
};

export default AccessControl;
